<template>
    <PageWrapper :loading="isLoading" title="Risk Profiles & Requirements" :description="description">
        <ConfirmationModal
            v-if="deleteId"
            additional-class-name="DecisioningCriteria__deleteConfirmation"
            title="Are you sure?"
            :progress="deletingRiskProfileStatus === OperationStatus.loading"
            @input="onConfirmationInput"
            @close="closeDeleteConfirmation"
        >
            <span>Please confirm you want to delete </span>
            <span class="text-eidTrustGreen">{{ deletingRiskProfileName }}</span>
        </ConfirmationModal>
        <RiskProfiles
            :risk-profiles="sortedRiskProfiles"
            @create="createRiskProfile"
            @copy="copyRiskProfile"
            @edit="editRiskProfile"
            @delete="deleteRiskProfile"
        />
    </PageWrapper>
</template>

<script lang="ts">
    import { Component, Vue, Watch } from '@evidentid/vue-property-decorator';

    import sortBy from 'lodash/sortBy';

    import { PendingScreen } from '@evidentid/dashboard-commons/screens/PendingScreen';
    import { RiskProfile } from '@evidentid/tprm-portal-lib/models/decisioning/RiskProfile.model';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import ConfirmationModal from '@/components/confirmation-modal/ConfirmationModal.vue';
    import PageWrapper from '@/components/page-wrapper/PageWrapper.vue';
    import CreateEditRiskProfile
        from '@/modules/decisioning-criteria/components/CreateEditRiskProfile/CreateEditRiskProfile.vue';
    import RiskProfileAssignment from '@/modules/decisioning-criteria/components/RiskProfileAssignment/RiskProfileAssignment.vue';
    import RiskProfiles
        from '@/modules/decisioning-criteria/components/RiskProfiles/RiskProfiles.vue';
    import {
        RiskProfileCopyDraftStatus,
        RiskProfilesStatus,
        createEmptyRiskProfileCopyDraftStatus,
        useDecisioningCriteriaStore,
    } from '@/modules/decisioning-criteria/store';
    import { usePageLayoutStore } from '@/modules/page-layout/store';

    @Component({
        components: {
            ConfirmationModal,
            RiskProfiles,
            CreateEditRiskProfile,
            RiskProfileAssignment,
            PageWrapper,
            PendingScreen,
        },
    })
    export default class DecisioningCriteria extends Vue {
        private get pageLayoutStore() {
            return usePageLayoutStore();
        }

        private lastRpName!: string;
        private deleteId: string | null = null;
        private OperationStatus = OperationStatus;
        private description = 'Define how compliance is evaluated. Create and manage risk profiles, set requirements, and control which rules apply to each entity.';

        private created(): void {
            this.pageLayoutStore.resetPageLayout();
        }

        @Watch('isLoading', { immediate: true })
        private onLoadingChange(): void {
            this.pageLayoutStore.setLoading(this.isLoading);
        }

        private get rpName(): string {
            return this.$rp.current!;
        }

        private get riskProfilesStatus(): RiskProfilesStatus {
            return useDecisioningCriteriaStore().riskProfiles[this.rpName] || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get deletingRiskProfileStatus(): OperationStatus {
            return useDecisioningCriteriaStore().deletingRiskProfileStatus[this.rpName] ||
                OperationStatus.uninitialized;
        }

        private get copyRiskProfileDraftStatus(): RiskProfileCopyDraftStatus {
            return useDecisioningCriteriaStore().copyRiskProfileDraft[this.rpName] ||
                createEmptyRiskProfileCopyDraftStatus();
        }

        private get isLoading(): boolean {
            return this.riskProfilesStatus.status === OperationStatus.loading ||
                this.copyRiskProfileDraftStatus.status === OperationStatus.loading;
        }

        private get deletingRiskProfileName(): string {
            return this.riskProfilesStatus.list.find((x) => x.id === this.deleteId)?.displayName || '';
        }

        private get sortedRiskProfiles(): RiskProfile[] {
            return sortBy(this.riskProfilesStatus.list, [ (riskProfile) => riskProfile.displayName.toLowerCase() ]);
        }

        @Watch('$rp.current', { immediate: true })
        private handleRpChange(rpName: string, prevRpName: string) {
            // Save information about the last resource, which should be cleared during destroy
            this.lastRpName = rpName;
            if (prevRpName) {
                useDecisioningCriteriaStore().clearRiskProfiles({ rpName: prevRpName });
            }
            this.loadRiskProfiles();
        }

        private loadRiskProfiles() {
            if (this.riskProfilesStatus.status !== OperationStatus.loading) {
                useDecisioningCriteriaStore().loadRiskProfiles({ rpName: this.rpName });
            }
        }

        private async onConfirmationInput(confirmed: boolean): Promise<void> {
            if (confirmed && this.deleteId && this.riskProfilesStatus.status !== OperationStatus.loading) {
                await useDecisioningCriteriaStore().deleteRiskProfile({
                    rpName: this.rpName,
                    riskProfileId: this.deleteId,
                });
            }
            this.closeDeleteConfirmation();
        }

        private closeDeleteConfirmation(): void {
            this.deleteId = null;
        }

        private createRiskProfile() {
            useDecisioningCriteriaStore().clearRiskProfileCopyDraft({ rpName: this.rpName });
            this.$router.push({
                name: 'createDecisioningRiskProfile',
                params: {
                    rpId: this.$route.params.rpId,
                },
            });
        }

        private editRiskProfile(id: string) {
            useDecisioningCriteriaStore().clearRiskProfileCopyDraft({ rpName: this.rpName });
            this.$router.push({
                name: 'decisioningRiskProfile',
                params: {
                    rpId: this.$route.params.rpId,
                    id,
                },
            });
        }

        private async copyRiskProfile(riskProfile: RiskProfile): Promise<void> {
            if (this.copyRiskProfileDraftStatus.status === OperationStatus.loading) {
                return;
            }
            await useDecisioningCriteriaStore().prepareRiskProfileCopy({
                rpName: this.rpName,
                riskProfile,
            });
            if (this.copyRiskProfileDraftStatus.status !== OperationStatus.success) {
                return;
            }
            this.$router.push({
                name: 'createDecisioningRiskProfile',
                params: {
                    rpId: this.$route.params.rpId,
                },
            });
        }

        private deleteRiskProfile(id: string) {
            this.deleteId = id;
        }

        private unmounted() {
            useDecisioningCriteriaStore().clearRiskProfiles({ rpName: this.lastRpName });
            useDecisioningCriteriaStore().clearDeletingRiskProfileStatus(
                { rpName: this.lastRpName });
        }
    }
</script>
